var _a;
import { __awaiter } from "tslib";
import { DataCollectionEventNames } from './plugins/datacollection';
import { EventServiceErrorType } from './plugins/eventservice';
import { AuthPluginErrorCode, TokenLifetimeRequirementType } from './plugins/auth';
import { eventServiceFacade } from './eventServiceFacade';
const cancelAuthMinimumInterfaceVersion = { major: 5, minor: 3, patch: 0 };
var PriorV4TokenLifetimeRequirementType;
(function (PriorV4TokenLifetimeRequirementType) {
    PriorV4TokenLifetimeRequirementType["requestTokenTimeoutAfter"] = "requestTokenTimeoutAfter";
    PriorV4TokenLifetimeRequirementType["tokenIsValidAtLeastFor"] = "tokenIsValidAtLeastFor";
})(PriorV4TokenLifetimeRequirementType || (PriorV4TokenLifetimeRequirementType = {}));
export class FacadeRegistry {
    static adaptJWeb(jWeb) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('FacadeRegistry::adaptJWeb');
            FacadeRegistry.facades.forEach((adaptFunction) => {
                adaptFunction(jWeb);
            });
            return jWeb;
        });
    }
    static createEventSubscriber(jWeb) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('FacadeRegistry::createEventSubscriber');
            if (jWeb.Plugins.EventService) {
                try {
                    const subscriber = yield jWeb.Plugins.EventService.createSubscriber();
                    if (FacadeRegistry.isSubscriber(subscriber)) {
                        return subscriber;
                    }
                }
                catch (e) {
                    console.error(e);
                }
            }
            return null;
        });
    }
    static isSubscriber(result) {
        return result.subscribe !== undefined;
    }
    static isLegacyDataCollectionPlugin(plugin) {
        return plugin.refreshCollectionProfile !== undefined
            && plugin.sendUiEvent !== undefined;
    }
    static addEventUnsubscriber(eventSubscriber) {
        console.log('FacadeRegistry::addEventUnsubscriber: %o', eventSubscriber);
        const globalThis = this.getGlobalThis();
        globalThis === null || globalThis === void 0 ? void 0 : globalThis.addEventListener('beforeunload', () => {
            console.log('FacadeRegistry::beforeunload');
        });
        globalThis === null || globalThis === void 0 ? void 0 : globalThis.addEventListener('visibilitychange', () => {
            console.log('FacadeRegistry::visibilitychange: %s', document.visibilityState);
        });
        globalThis === null || globalThis === void 0 ? void 0 : globalThis.addEventListener('pagehide', (event) => {
            console.log('FacadeRegistry::pagehide: %s', event.persisted ? 'persisted' : 'not persisted');
            if (!event.persisted) {
                eventSubscriber.unsubscribe();
            }
        });
        globalThis === null || globalThis === void 0 ? void 0 : globalThis.addEventListener('pageshow', (event) => {
            console.log('FacadeRegistry::pageshow: %s', event.persisted ? 'persisted' : 'not persisted');
        });
    }
    static subscriberIdNotSupportedError() {
        console.warn('Creating a subscriber with a specific ID is not supported.');
        const error = {
            errorType: EventServiceErrorType.subscriberIdNotSupported,
            reason: 'Creating a subscriber with a specific ID is not supported.'
        };
        return error;
    }
    static isAccountProviderAuthPlugin(authPlugin) {
        return !authPlugin.getSupportedInterfaceVersion;
    }
    static isGetTokenAccessToken(getTokenResult) {
        return getTokenResult.tokenValue !== undefined;
    }
    static pluginSupportsInterfaceVersion(plugin, interfaceVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!plugin.getSupportedInterfaceVersion) {
                return false;
            }
            const pluginVersion = yield plugin.getSupportedInterfaceVersion();
            return pluginVersion.major > interfaceVersion.major ||
                (pluginVersion.major === interfaceVersion.major && pluginVersion.minor > interfaceVersion.minor) ||
                (pluginVersion.major === interfaceVersion.major && pluginVersion.minor === interfaceVersion.minor && pluginVersion.patch >= interfaceVersion.patch);
        });
    }
}
_a = FacadeRegistry;
FacadeRegistry.getGlobalThis = () => {
    if (typeof self !== 'undefined') {
        return self;
    }
    if (typeof window !== 'undefined') {
        return window;
    }
    if (typeof global !== 'undefined') {
        return global;
    }
    return null;
};
FacadeRegistry.getPersistentUnsupportedError = (reason) => ({
    errorType: EventServiceErrorType.persistentSubscribersNotSupported,
    reason
});
FacadeRegistry.subscriberNotPersistent = {
    errorType: EventServiceErrorType.subscriberNotPersistent,
    reason: 'Subscriber was created without persistent option set'
};
// jweb-core version that the Auth plugin interface was last updated.
FacadeRegistry.authFacadeVersion = {
    major: 5,
    minor: 2,
    patch: 0
};
// Returns the current stored access token if it's valid based on tokenLifetimeRequirements.
FacadeRegistry.currentAccessTokenIsValid = (currentToken, options) => {
    var _b, _c, _d;
    let requirements = (_c = (_b = options.tokenProviderOptions) === null || _b === void 0 ? void 0 : _b.tokenLifetimeRequirements) !== null && _c !== void 0 ? _c : [];
    // By default, if deprecated requireFreshToken is set,
    // treat it as preferredMinimumSecondsUntilExpiration with 30 minutes as time interval.
    if ((_d = options.tokenProviderOptions) === null || _d === void 0 ? void 0 : _d.requireFreshToken) {
        requirements = [
            ...requirements,
            {
                type: TokenLifetimeRequirementType.preferredMinimumSecondsUntilExpiration,
                timeInterval: 1800,
            },
        ];
    }
    // Validates the current token by checking if it follows all requirements.
    return requirements.every((req) => {
        if (req.type === TokenLifetimeRequirementType.preferredMinimumSecondsUntilExpiration) {
            const currentTime = new Date();
            // expiresAt must be an ISO date as a string.
            const expiresAt = new Date(currentToken.expiresAt);
            currentTime.setSeconds(currentTime.getSeconds() + req.timeInterval);
            return currentTime <= expiresAt;
        }
        return true;
    });
};
// Returns requireFreshToken value to passed to old auth plugin versions.
FacadeRegistry.getRequireFreshTokenFromTokenOptions = (options) => {
    var _b, _c;
    if ((_b = options.tokenProviderOptions) === null || _b === void 0 ? void 0 : _b.requireFreshToken) {
        return options.tokenProviderOptions.requireFreshToken;
    }
    else if ((_c = options.tokenProviderOptions) === null || _c === void 0 ? void 0 : _c.tokenLifetimeRequirements) {
        return options.tokenProviderOptions.tokenLifetimeRequirements.some((req) => req.type ===
            TokenLifetimeRequirementType.preferredMaximumSecondsSinceIssued &&
            req.timeInterval === 0);
    }
    else {
        return undefined;
    }
};
// Adapts the getToken call to handle the old auth plugin implementation of the GetTokenOptions
// interface with accountProviderOptions instead of tokenProviderOptions and the one that uses
// the requireFreshToken property and does not support tokenLifetimeRequirements.
FacadeRegistry.adaptAuthPluginGetTokenCall = (authPlugin, options) => __awaiter(void 0, void 0, void 0, function* () {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
    // No need to adapt options when undefined.
    if (!options) {
        return authPlugin.getToken();
    }
    // Adapt options to additionalAuthorizationParameters when getSupportedInterfaceVersion is not implemented.
    const supportAdditionalAuthorizationParameters = yield FacadeRegistry.pluginSupportsInterfaceVersion(authPlugin, { major: 4, minor: 0, patch: 3, });
    const additionalAuthorizationParameters = (_b = options === null || options === void 0 ? void 0 : options.tokenProviderOptions) === null || _b === void 0 ? void 0 : _b.additionalAuthorizationParameters;
    if (supportAdditionalAuthorizationParameters && additionalAuthorizationParameters) {
        console.warn('FacadeRegistry::adaptAuthPluginGetToken: Cannot confirm if additionalAuthorizationParameters are supported', additionalAuthorizationParameters);
    }
    //Adapt for orgless token
    const supportOrglessToken = yield FacadeRegistry.pluginSupportsInterfaceVersion(authPlugin, { major: 5, minor: 0, patch: 0, });
    const orglessToken = (_c = options === null || options === void 0 ? void 0 : options.tokenProviderOptions) === null || _c === void 0 ? void 0 : _c.tenantID;
    if (supportOrglessToken && orglessToken === null) {
        console.warn('FacadeRegistry::adaptAuthPluginGetToken: Cannot confirm if orgless token is supported', orglessToken);
    }
    // Adapt options to accountProviderOptions when getSupportedInterfaceVersion is not implemented.
    if (FacadeRegistry.isAccountProviderAuthPlugin(authPlugin)) {
        if (options.tokenProviderOptions && options.tokenProviderOptions.tokenType === 'device') {
            console.warn('FacadeRegistry::adaptAuthPluginGetToken: Auth Fallback was not allowed.');
            const error = {
                error: {
                    code: AuthPluginErrorCode.invalidOption,
                    reason: 'Legacy plugin dose not support get device token',
                },
            };
            return Promise.resolve(error);
        }
        console.log('FacadeRegistry::adaptAuthPluginGetToken: Convert options to accountProviderOptions', options);
        // Get an adapted requireFreshToken value.
        let requireFreshToken = FacadeRegistry.getRequireFreshTokenFromTokenOptions(options);
        // If it's requesting a user token and tokenLifetimeRequirements are passed, we must
        // request the current stored access token and validate it.
        // - if the token it's valid, we will return to the webapp.
        // - it it's not, we force token refresh.
        if (options.tokenProviderOptions &&
            options.tokenProviderOptions.tokenLifetimeRequirements &&
            options.tokenProviderOptions.tokenLifetimeRequirements.length) {
            const getTokenResult = (yield authPlugin.getToken());
            if (FacadeRegistry.isGetTokenAccessToken(getTokenResult)) {
                if (FacadeRegistry.currentAccessTokenIsValid(getTokenResult, options)) {
                    return Promise.resolve(Object.assign(Object.assign({}, getTokenResult), { tokenOptions: options.tokenProviderOptions })); // Return current AccessToken.
                }
                else {
                    requireFreshToken = true;
                }
            }
            else {
                return Promise.resolve(getTokenResult); // Return AuthPluginError.
            }
        }
        const accountProviderOptions = {
            allowNetworkAccess: (_d = options.tokenProviderOptions) === null || _d === void 0 ? void 0 : _d.allowNetworkAccess,
            allowUserInteraction: (_e = options.tokenProviderOptions) === null || _e === void 0 ? void 0 : _e.allowUserInteraction,
            showAccountCreationLink: (_f = options.tokenProviderOptions) === null || _f === void 0 ? void 0 : _f.showAccountCreationLink,
            skipTokenRefresh: (_g = options.tokenProviderOptions) === null || _g === void 0 ? void 0 : _g.skipTokenRefresh,
            userInteractionEntryPoint: (_h = options.tokenProviderOptions) === null || _h === void 0 ? void 0 : _h.userInteractionEntryPoint,
            requireFreshToken,
        };
        const getTokenOptions = {
            accountProviderOptions,
        };
        return authPlugin.getToken(getTokenOptions);
    }
    // Adapt to support requireFreshToken when getSupportedInterfaceVersion returns an older version.
    const supportTokenLifetimeRequirements = yield FacadeRegistry.pluginSupportsInterfaceVersion(authPlugin, { major: 3, minor: 1, patch: 0, });
    if (!supportTokenLifetimeRequirements) {
        authPlugin = authPlugin;
        console.log('FacadeRegistry::adaptAuthPluginGetToken: Support legacy requireFreshToken', options);
        // Get an adapted requireFreshToken value.
        let requireFreshToken = FacadeRegistry.getRequireFreshTokenFromTokenOptions(options);
        // If it's requesting a user token and tokenLifetimeRequirements are passed, we must
        // request the current stored access token and validate it.
        // - if the token it's valid, we will return to the webapp.
        // - it it's not, we force token refresh.
        if (options.tokenProviderOptions &&
            options.tokenProviderOptions.tokenLifetimeRequirements &&
            options.tokenProviderOptions.tokenLifetimeRequirements.length) {
            const getTokenResult = yield authPlugin.getToken({
                tokenProviderOptions: { tokenType: options.tokenProviderOptions.tokenType },
            });
            if (FacadeRegistry.isGetTokenAccessToken(getTokenResult)) {
                if (FacadeRegistry.currentAccessTokenIsValid(getTokenResult, options)) {
                    return Promise.resolve(Object.assign(Object.assign({}, getTokenResult), { tokenOptions: options.tokenProviderOptions })); // Return current AccessToken.
                }
                else {
                    requireFreshToken = true;
                }
            }
            else {
                return Promise.resolve(getTokenResult); // Return AuthPluginError.
            }
        }
        const tokenProviderOptions = {
            tokenType: (_j = options.tokenProviderOptions) === null || _j === void 0 ? void 0 : _j.tokenType,
            allowNetworkAccess: (_k = options.tokenProviderOptions) === null || _k === void 0 ? void 0 : _k.allowNetworkAccess,
            allowUserInteraction: (_l = options.tokenProviderOptions) === null || _l === void 0 ? void 0 : _l.allowUserInteraction,
            showAccountCreationLink: (_m = options.tokenProviderOptions) === null || _m === void 0 ? void 0 : _m.showAccountCreationLink,
            skipTokenRefresh: (_o = options.tokenProviderOptions) === null || _o === void 0 ? void 0 : _o.skipTokenRefresh,
            userInteractionEntryPoint: (_p = options.tokenProviderOptions) === null || _p === void 0 ? void 0 : _p.userInteractionEntryPoint,
            printerUuid: (_q = options.tokenProviderOptions) === null || _q === void 0 ? void 0 : _q.printerUuid,
            scopesRequested: (_r = options.tokenProviderOptions) === null || _r === void 0 ? void 0 : _r.scopesRequested,
            tenantID: (_s = options.tokenProviderOptions) === null || _s === void 0 ? void 0 : _s.tenantID,
            requireFreshToken,
        };
        const getTokenOptions = {
            tokenProviderOptions,
        };
        return authPlugin.getToken(getTokenOptions);
    }
    // Adapt to support tokenLifetimeRequirements old naming.
    const supportTokenLifetimeRequirementsNewNaming = yield FacadeRegistry.pluginSupportsInterfaceVersion(authPlugin, { major: 3, minor: 2, patch: 0, });
    if (!supportTokenLifetimeRequirementsNewNaming) {
        const oldAuthPlugin = authPlugin;
        console.log('FacadeRegistry::adaptAuthPluginGetToken: Support legacy tokenLifetimeRequirements naming', options);
        // Replace current requirements with respective old requirements.
        const requirements = (_u = (_t = options.tokenProviderOptions) === null || _t === void 0 ? void 0 : _t.tokenLifetimeRequirements) !== null && _u !== void 0 ? _u : [];
        const adaptedRequirements = requirements.map((req) => {
            switch (req.type) {
                case TokenLifetimeRequirementType.preferredMaximumSecondsSinceIssued:
                    return {
                        type: PriorV4TokenLifetimeRequirementType.requestTokenTimeoutAfter,
                        timeInterval: req.timeInterval,
                    };
                case TokenLifetimeRequirementType.preferredMinimumSecondsUntilExpiration:
                    return {
                        type: PriorV4TokenLifetimeRequirementType.tokenIsValidAtLeastFor,
                        timeInterval: req.timeInterval,
                    };
            }
        });
        const getTokenOptions = {
            tokenProviderOptions: Object.assign(Object.assign({}, options.tokenProviderOptions), { tokenLifetimeRequirements: adaptedRequirements }),
        };
        return oldAuthPlugin.getToken(getTokenOptions);
    }
    if ((_v = options.tokenProviderOptions) === null || _v === void 0 ? void 0 : _v.requestId) {
        const supportCancelRequest = yield FacadeRegistry.pluginSupportsInterfaceVersion(authPlugin, cancelAuthMinimumInterfaceVersion);
        if (!supportCancelRequest) {
            const message = `Removing 'requestId' from options. Cancel functionality is not supported below interface version ${JSON.stringify(cancelAuthMinimumInterfaceVersion)}.`;
            console.warn(`FacadeRegistry::adaptAuthPluginGetToken: ${message}`);
            const newTokenProviderOptions = options.tokenProviderOptions;
            delete newTokenProviderOptions.requestId;
            return authPlugin.getToken({
                tokenProviderOptions: newTokenProviderOptions,
            });
        }
    }
    // No need to adapt options.
    return authPlugin.getToken(options);
});
// This facade handles old auth plugin implementations of deprecated interface versions.
FacadeRegistry.authFallbackFacade = (jWeb) => __awaiter(void 0, void 0, void 0, function* () {
    if (!jWeb.isPluginAvailable('Auth')) {
        // Cannot adapt Auth if Auth is not available
        return;
    }
    const authPlugin = jWeb.Plugins.Auth;
    const authFacade = {
        getSupportedInterfaceVersion: () => Promise.resolve(FacadeRegistry.authFacadeVersion),
        isLoggedIn: authPlugin.isLoggedIn,
        logout: authPlugin.logout,
        addListener: authPlugin.addListener,
        getToken: (options) => FacadeRegistry.adaptAuthPluginGetTokenCall(authPlugin, options),
        cancelRequest: (options) => __awaiter(void 0, void 0, void 0, function* () {
            const supportCancelRequest = yield FacadeRegistry.pluginSupportsInterfaceVersion(authPlugin, cancelAuthMinimumInterfaceVersion);
            if (!supportCancelRequest) {
                const message = `Cancel request is not supported below interface version ${JSON.stringify(cancelAuthMinimumInterfaceVersion)}.`;
                const error = {
                    error: {
                        code: AuthPluginErrorCode.cancellationError,
                        reason: message
                    }
                };
                console.warn(`FacadeRegistry::authFacade: ${message}`);
                return Promise.resolve(error);
            }
            else {
                return authPlugin.cancelRequest(options);
            }
        })
    };
    jWeb.Plugins.Auth = authFacade;
});
// This facade handles old auth implementations that use plugin listeners to broadcast login state changes and
// transforms those broadcasts into published events using the Event Service.
FacadeRegistry.authLoginStateListenerFacade = (jWeb) => __awaiter(void 0, void 0, void 0, function* () {
    var _w, _x;
    if (!jWeb.isPluginAvailable('Auth') || !jWeb.isPluginAvailable('EventService')) {
        return;
    }
    const authPlugin = (_w = jWeb.Plugins) === null || _w === void 0 ? void 0 : _w.Auth;
    const eventServicePlugin = (_x = jWeb.Plugins) === null || _x === void 0 ? void 0 : _x.EventService;
    const publisherId = "com.hp.jarvis.auth.event.publisher";
    const eventName = "com.hp.jarvis.auth.event.login.change";
    const legacyEventName = "LoginChange";
    const isEventServiceError = (result) => {
        return result.errorType !== undefined;
    };
    const addTransformer = () => {
        eventServicePlugin === null || eventServicePlugin === void 0 ? void 0 : eventServicePlugin.createPublisher(publisherId).then((result) => {
            if (isEventServiceError(result)) {
                console.error("FacadeRegistry::authLegacyListenerFacade: Create publisher: %o", result);
                return;
            }
            authPlugin === null || authPlugin === void 0 ? void 0 : authPlugin.addListener(legacyEventName, (loginState) => {
                if (loginState.isLoggedIn && loginState.account === undefined) {
                    console.error("FacadeRegistry::authLegacyListenerFacade: Invalid 'LoginState': %o", loginState);
                    return;
                }
                const eventData = {
                    currentAccount: loginState.isLoggedIn ? loginState.account : null,
                    previousAccount: undefined,
                };
                result
                    .publish(eventName, eventData)
                    .catch((error) => console.error("FacadeRegistry::authLegacyListenerFacade: Republish event: %o", error));
            });
        });
    };
    const usesEventService = yield FacadeRegistry.pluginSupportsInterfaceVersion(authPlugin, { major: 1, minor: 8, patch: 0 });
    if (!usesEventService) {
        addTransformer();
    }
});
// Facade that adds automatic subscription unsubscribe functionality to each subscriber
FacadeRegistry.subscriptionUnsubscribeFacade = (jWeb) => {
    if (!jWeb.isPluginAvailable('EventService')) {
        // Cannot unsubscribe automatically if EventService is not available
        return;
    }
    const eventServicePlugin = jWeb.Plugins.EventService;
    // Automatically unsubscribe event subscribers during page unloading
    if (eventServicePlugin.createSubscriber) {
        const createSubscriberOriginal = eventServicePlugin.createSubscriber;
        eventServicePlugin.createSubscriber = (options) => __awaiter(void 0, void 0, void 0, function* () {
            const subscriberResult = yield createSubscriberOriginal(options);
            if (FacadeRegistry.isSubscriber(subscriberResult)) {
                FacadeRegistry.addEventUnsubscriber(subscriberResult);
            }
            return subscriberResult;
        });
    }
    jWeb.Plugins.EventService = eventServicePlugin;
};
// Facade to transform legacy data collection calls to latest interface
FacadeRegistry.legacyDataCollectionFacade = (jWeb) => {
    if (!jWeb.isPluginAvailable('DataCollection') || !jWeb.isPluginAvailable('EventService')) {
        // Do not want to adapt DataCollection if it was not injected in the first place
        // Cannot adapt DataCollection if EventService is not available
        return;
    }
    const dataCollectionPlugin = jWeb.Plugins.DataCollection;
    if (!FacadeRegistry.isLegacyDataCollectionPlugin(dataCollectionPlugin)) {
        return;
    }
    // If the native side injects the legacy data collection method..
    dataCollectionPlugin.invalidateCache = dataCollectionPlugin.refreshCollectionProfile;
    if (dataCollectionPlugin.sendUiEvent) {
        FacadeRegistry.createEventSubscriber(jWeb).then((subscriber) => {
            // Handle EventService communicating with app that has old data collection component
            if (subscriber) {
                // Register our listener
                const subscribeOptions = {
                    eventName: DataCollectionEventNames.cdmEvent
                };
                subscriber.subscribe(subscribeOptions, (eventInfo) => {
                    // Check if this is a simpleUi event!
                    try {
                        const dataValveCDMEventData = eventInfo.eventData;
                        dataValveCDMEventData.events.forEach((cdmEventData) => {
                            if (cdmEventData.eventCategory === 'simpleUi') {
                                const simpleUiEvent = (cdmEventData.eventDetail);
                                console.log('FacadeRegistry::legacyDataCollectionFacade: Forwarding Simple UI event to legacy DataCollection plugin: %o', simpleUiEvent);
                                dataCollectionPlugin.sendUiEvent(simpleUiEvent).then(() => {
                                    console.log('FacadeRegistry::legacyDataCollectionFacade: Successfully forwarded Simple UI event to legacy DataCollection plugin');
                                }).catch(error => {
                                    console.error('FacadeRegistry::legacyDataCollectionFacade: Error forwarding Simple UI event to legacy DataCollection plugin: %o', error);
                                });
                            }
                        });
                    }
                    catch (e) {
                        console.error(e);
                    }
                });
            }
        });
    }
};
// Facade to handle older version of EventService by adding pause/resume subscriber methods that return error
FacadeRegistry.eventPersistenceFacade = (jWeb) => {
    if (!jWeb.isPluginAvailable('EventService')) {
        // Cannot update methods if EventService is not available
        return;
    }
    const eventServicePlugin = jWeb.Plugins.EventService;
    if (eventServicePlugin.createSubscriber) {
        const createSubscriberOriginal = eventServicePlugin.createSubscriber;
        eventServicePlugin.createSubscriber = (options) => __awaiter(void 0, void 0, void 0, function* () {
            const subscriberResult = yield createSubscriberOriginal(options);
            if (FacadeRegistry.isSubscriber(subscriberResult)) {
                const isMissingPersistenceMethods = subscriberResult.pauseEvents === undefined || subscriberResult.resumeEvents === undefined;
                if (isMissingPersistenceMethods) {
                    if (options === null || options === void 0 ? void 0 : options.isPersistent) {
                        return FacadeRegistry.getPersistentUnsupportedError('Host app does not support persistent subscribers in injected EventService plugin');
                    }
                    else {
                        console.log('FacadeRegistry::eventPersistenceFacade: Attaching persistence methods');
                        subscriberResult.pauseEvents = () => Promise.resolve(FacadeRegistry.subscriberNotPersistent);
                        subscriberResult.resumeEvents = () => Promise.resolve(FacadeRegistry.subscriberNotPersistent);
                    }
                }
            }
            return subscriberResult;
        });
    }
    jWeb.Plugins.EventService = eventServicePlugin;
};
// Facade to handle older version of EventService by identifying if user is sending a subscriberId and if so returns an error.
FacadeRegistry.createSubscriberWithIdFacade = (jWeb) => {
    if (!jWeb.isPluginAvailable('EventService')) {
        // Cannot update methods if EventService is not available
        return;
    }
    const eventServicePlugin = jWeb.Plugins.EventService;
    if (eventServicePlugin.createSubscriber) {
        const createSubscriberOriginal = eventServicePlugin.createSubscriber;
        eventServicePlugin.createSubscriber = (options) => __awaiter(void 0, void 0, void 0, function* () {
            const subscriberResult = yield createSubscriberOriginal(options);
            if (FacadeRegistry.isSubscriber(subscriberResult) && (options === null || options === void 0 ? void 0 : options.subscriberId)) {
                const haveTheSameSubscriberId = subscriberResult.subscriberId === options.subscriberId;
                if (!haveTheSameSubscriberId) {
                    return FacadeRegistry.subscriberIdNotSupportedError();
                }
            }
            return subscriberResult;
        });
    }
    jWeb.Plugins.EventService = eventServicePlugin;
};
// Fixed order of facades.
// These facades are applied sequentially
FacadeRegistry.facades = [
    FacadeRegistry.subscriptionUnsubscribeFacade,
    FacadeRegistry.legacyDataCollectionFacade,
    FacadeRegistry.eventPersistenceFacade,
    FacadeRegistry.createSubscriberWithIdFacade,
    FacadeRegistry.authLoginStateListenerFacade,
    FacadeRegistry.authFallbackFacade,
    eventServiceFacade
];
//# sourceMappingURL=facade-registry.js.map